import { resolveAssignmentRosterContext } from '../../SolverShared/manualOverride';

interface MatrixPositionLike {
    position_number: number;
    status?: string;
    employee?: {
        id: number;
        name?: string;
        code?: string;
    } | null;
    roster_date?: string | null;
    shift_id?: number | null;
    assignment_start?: string | null;
    assignment_end?: string | null;
}

interface MatrixOperationLike {
    operation_plan_id: number;
    operation_name?: string | null;
    planned_start?: string | null;
    planned_end?: string | null;
    positions?: MatrixPositionLike[];
}

interface MatrixShiftAssignmentLike {
    employee_id: number;
    employee_name?: string | null;
    employee_code?: string | null;
    date: string;
    shift_id: number;
    start_time?: string | null;
    end_time?: string | null;
    attendance_kind?: string | null;
    plan_type?: string | null;
    nominal_hours?: number | null;
}

interface PersistedAssignmentLike {
    operation_plan_id: number;
    position_number: number;
    employee_id: number;
    employee_name?: string | null;
    employee_code?: string | null;
    operation_name?: string | null;
    planned_start?: string | null;
    planned_end?: string | null;
    date?: string | null;
    shift_id?: number | null;
}

export interface MatrixOperationAssignment {
    employee_id: number;
    employee_name: string;
    employee_code: string;
    operation_name: string;
    date: string;
    shift_id: number | null;
    planned_start: string;
    planned_end: string;
}

const datePart = (value: unknown): string => (
    typeof value === 'string' ? value.slice(0, 10) : ''
);

/**
 * Build the operation overlay from the current editable positions.
 *
 * `persistedAssignments` is the immutable solver snapshot and is used only to
 * recover precise execution/roster context. The employee identity must come
 * from `operations[].positions`, which is the result page's editable state.
 */
export const deriveMatrixOperationAssignments = (
    operations: MatrixOperationLike[] | undefined,
    shiftAssignments: MatrixShiftAssignmentLike[],
    persistedAssignments: PersistedAssignmentLike[],
): MatrixOperationAssignment[] => {
    if (!Array.isArray(operations)) {
        return persistedAssignments
            .map((assignment) => ({
                employee_id: assignment.employee_id,
                employee_name: assignment.employee_name || '',
                employee_code: assignment.employee_code || '',
                operation_name: assignment.operation_name || '未知操作',
                date: assignment.date
                    ? String(assignment.date).slice(0, 10)
                    : datePart(assignment.planned_start),
                shift_id: assignment.shift_id == null ? null : Number(assignment.shift_id),
                planned_start: assignment.planned_start || '',
                planned_end: assignment.planned_end || '',
            }))
            .filter((assignment) => Boolean(assignment.date));
    }

    const result: MatrixOperationAssignment[] = [];
    operations.forEach((operation) => {
        (operation.positions || []).forEach((position) => {
            if (
                position.status !== 'ASSIGNED'
                || !position.employee
                || !Number.isInteger(position.employee.id)
            ) {
                return;
            }
            const rosterContext = position.roster_date && position.shift_id
                ? {
                    date: String(position.roster_date).slice(0, 10),
                    shift_id: Number(position.shift_id),
                }
                : resolveAssignmentRosterContext(
                    position.employee.id,
                    operation.operation_plan_id,
                    position.position_number,
                    operation,
                    shiftAssignments,
                    persistedAssignments,
                );
            const date = rosterContext?.date || datePart(operation.planned_start);
            if (!date) return;

            result.push({
                employee_id: position.employee.id,
                employee_name: position.employee.name || '',
                employee_code: position.employee.code || '',
                operation_name: operation.operation_name || '未知操作',
                date,
                shift_id: rosterContext?.shift_id || null,
                planned_start: position.assignment_start || operation.planned_start || '',
                planned_end: position.assignment_end || operation.planned_end || '',
            });
        });
    });
    return result;
};
